import { request } from '@umijs/max';

/** 整任务取消状态；拒绝后保留本轮历史并允许重新申请。 */
export type TaskCancelStatus = 'PENDING_CANCEL_APPROVAL' | 'CANCELLING' | 'CANCELLED' | 'REJECTED';
/** 取消详情与资格，由服务端核实节点受理关系。 */
export interface TaskCancelDetail {
  /** 正式任务ID，沿用长整型字符串传输。 */
  taskId: string;
  /** 最近一轮申请ID，未申请为空。 */
  applicationId?: string;
  /** 当前申请状态，未申请为空。 */
  status?: TaskCancelStatus;
  /** 当前办理版本，提交动作时原样传回。 */
  version?: number;
  /** 当前用户是否可以申请取消。 */
  canApply: boolean;
  /** 不能申请的原因。 */
  unavailableReason?: string;
  /** 当前用户能否办理部门经理审批，允许本人审批。 */
  canApprove: boolean;
  /** 当前用户能否办理客户确认。 */
  canConfirm: boolean;
  /** 当前轮次原因，仅授权详情返回。 */
  reason?: string;
  /** 客户确认截止时间，Unix毫秒。 */
  deadline?: number | string;
  /** 同机构有效部门经理，包含具备资格的本人。 */
  approvers: TaskCancelApprover[];
  /** 现有工作流审批轨迹，包含取消申请及办理意见。 */
  records: TaskCancelHistory[];
}
/** 部门经理最小选人信息。 */
export interface TaskCancelApprover {
  /** 有效人员ID，允许申请人本人。 */
  id: string;
  /** 显示姓名。 */
  name: string;
}
/** 当前申请工作流记录，只向授权参与人展示。 */
export interface TaskCancelHistory {
  /** 记录ID。 */
  id?: string;
  /** 工作流动作名称。 */
  actionName?: string;
  /** 申请或审批意见。 */
  comment?: string;
  /** 记录时间，格式yyyy-MM-dd HH:mm:ss，北京时间。 */
  createTime?: string;
  /** 实际操作人；系统定时器可无显示姓名。 */
  creator?: TaskCancelActor;
}
/** 工作流记录的最小操作人标识。 */
export interface TaskCancelActor {
  /** 用户ID，系统任务可为0。 */
  id?: string;
  /** 已解析姓名，系统任务可为空。 */
  name?: string;
}
/** 后端响应封装。 */
interface Response<T> {
  /** 是否成功。 */
  success: boolean;
  /** 成功时的数据。 */
  result?: T;
  /** 失败提示。 */
  message?: string;
}
/**
 * 统一检查业务结果，失败时交由界面保留输入并提示。
 * @param response 后端业务结果
 * @returns 成功数据，失败时抛出业务错误
 */
function result<T>(response: Response<T>): T {
  if (!response.success || response.result == null) throw new Error(response.message || '任务取消操作失败');
  return response.result;
}
/**
 * 查询正式任务的取消资格和最近一轮历史。
 * @param taskId 正式任务ID
 * @param applicationId 指定历史轮次，为空时查询最近申请
 * @returns 服务端核实的资格及申请进度
 */
export async function getTaskCancelDetail(taskId: string | number, applicationId?: string): Promise<TaskCancelDetail> {
  return result(await request<Response<TaskCancelDetail>>('/ccb-ai/taskCancel/detail', { params: { taskId, ...(applicationId ? { applicationId } : {}) } }));
}
/**
 * 发起工作流申请，审批人允许为本人。
 * @param taskId 当前操作人的正式任务ID
 * @param reason 必填取消原因，最多1000字
 * @param approverId 同机构有效部门经理ID，允许本人
 * @returns 待审批申请，提交后暂停原业务办理
 */
export async function applyTaskCancel(taskId: string | number, reason: string, approverId: string): Promise<TaskCancelDetail> {
  return result(await request<Response<TaskCancelDetail>>('/ccb-ai/taskCancel/apply', {
    method: 'POST', data: { taskId, reason, approverId },
  }));
}
/**
 * 办理当前申请，版本防止旧页面重复审批。
 * @param detail 当前申请及版本快照
 * @param agree 是否同意当前取消申请
 * @param comment 办理意见，部门经理驳回时必填
 * @returns 办理后的申请状态
 */
export async function processTaskCancel(detail: TaskCancelDetail, agree: boolean, comment: string): Promise<TaskCancelDetail> {
  return result(await request<Response<TaskCancelDetail>>('/ccb-ai/taskCancel/process', {
    method: 'POST', data: { applicationId: detail.applicationId, version: detail.version, agree, comment },
  }));
}
/** 页面取消状态文案。 */
export const TASK_CANCEL_NAMES: Record<TaskCancelStatus, string> = {
  PENDING_CANCEL_APPROVAL: '取消审批中', CANCELLING: '取消中', CANCELLED: '已取消', REJECTED: '取消未通过',
};
